// Copyright (c) 2020-2021, 2024 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import DeleteIcon from '@mui/icons-material/Delete';
import { Field } from 'mobx-react-form';
import { observer } from 'mobx-react';
import { action, makeObservable, observable } from 'mobx';
import cx from 'classnames';

import { getBase64FromFile } from '@shared/utils/common';
import FileUpload, { FileUploadProps } from '@shared/components/FileUpload';
import Cropper from '@shared/components/Cropper';

// Prop interface for the main class.
export interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  className?: string;
  classes?: Record<string, any>;
  field?: Field;
  fileUploadProps?: Partial<FileUploadProps>;
  withUpload?: boolean;
  withCrop?: boolean;
  onChange?: (imgData) => any;
}

export const DEFAULT_FILE_UPLOAD_PROPS: Omit<FileUploadProps, 'onUpload' | 'classes'> = {
  placeholder: 'Upload photo',
  config: {
    maxSize: 2097152,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg', '.png']
    },
    multiple: false,
  }
};

// Class to upload an image and show it.
@observer
class Image extends React.Component<ImageProps> {
  @observable isCropperOpen = false;
  @observable hasError = false;
  @observable loading = true;
  private uploadedImageSrc: string;

  constructor(props: ImageProps) {
    super(props);
    makeObservable(this);
  }

  // Getter for the image source.
  private get src(): string {
    const { field, src } = this.props;

    return field ? field.value : src;
  }

  private handleUpload = async (file: File) => {
    const { withCrop, onChange } = this.props;
    const src = await getBase64FromFile(file);

    if (onChange) {
      onChange(src);
    }

    if (withCrop) {
      this.uploadedImageSrc = src;
      this.openCropper();

      return;
    }

    this.setFieldValue(src);
  }

  private handleDelete = () => {
    const { onChange } = this.props;
    const emptyValue = '';

    if (onChange) {
      onChange(emptyValue);
    }

    this.setFieldValue(emptyValue);
  }

  private handleCropApply = (croppedImage: ImageProps['src']) => {
    this.setFieldValue(croppedImage);
  }

  private setFieldValue = (data: ImageProps['src']) => {
    const { field } = this.props;

    if (field) {
      field.set('value', data);
    }
  }

  @action openCropper = () => {
    this.isCropperOpen = true;
  }

  @action closeCropper = () => {
    this.isCropperOpen = false;
  }

  private editImage = () => {
    this.uploadedImageSrc = this.src;
    this.openCropper();
  }

  @action handleError = () => {
    this.hasError = true;
    this.loading = false;
  }

  @action handleLoaded = () => {
    this.loading = false;
  }

  private get fileUploadProps(): Omit<FileUploadProps, 'onUpload' | 'classes'> {
    const { fileUploadProps } = this.props;

    return {
      ...DEFAULT_FILE_UPLOAD_PROPS,
      ...fileUploadProps,
      config: {
        ...DEFAULT_FILE_UPLOAD_PROPS.config,
        ...fileUploadProps?.config
      }
    };
  }

  render() {
    const { withUpload, withCrop } = this.props;
    const classes = withStyles.getClasses(this.props);

    if (!withUpload && this.hasError) {
      return (
        <span>No image</span>
      );
    }

    if (this.isCropperOpen) {
      return (
        <Cropper
          src={this.uploadedImageSrc}
          uploadProps={this.fileUploadProps}
          onApply={this.handleCropApply}
          close={this.closeCropper}
        />
      );
    }

    if (withUpload && !this.src) {
      return (
        <FileUpload
          onUpload={this.handleUpload}
          {...this.fileUploadProps}
        />
      );
    }

    if (withUpload && this.src) {
      return (
        <div className={classes.imgWrapper}>
          <img
            src={this.src}
            className={cx(classes.root, { [classes.rootWithCrop]: withCrop })}
            onClick={this.editImage}
          />
          <DeleteIcon className={classes.deleteIcon} onClick={this.handleDelete} />
        </div>
      );
    }

    return (
      <img
        src={this.src}
        className={cx(classes.root, {
          [classes.show]: !this.loading,
          [classes.hide]: this.loading,
        })}
        onError={this.handleError}
        onLoad={this.handleLoaded}
      />
    );
  }
}

const ImageStyled = withStyles(
  Image, 
  (theme, props) => ({
    "root": {
      objectFit: 'cover',
      width: 120,
      height: 120,
      borderRadius: '4px',
    },
    "imgWrapper": {
      display: 'flex',
      alignItems: 'center'
    },
    "deleteIcon": {
      margin: theme.spacing(1, 0, 0, 2),
      color: theme.palette.colors.red,
  
      '&:hover': {
        cursor: 'pointer',
      }
    },
    "rootWithCrop": {
      cursor: 'pointer',
    },
    "show": {
      display: 'block',
    },
    "hide": {
      display: 'none',
    }
  })
);

export default ImageStyled;